/**
 * useUserRoles – Fetch tất cả roles của user từ user_role_assignments
 * Trả về primary role + all role codes + isMultiRole
 * Không phụ thuộc useUserRoleAssignments cũ (gọi supabase trực tiếp)
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/core/database/supabase';
import type { UserRole } from '@/config/navigation.config';

export interface UserRolesReturn {
  /** Role chính từ user_profiles */
  primaryRole: UserRole;
  /** Tất cả role codes (deduped) */
  allRoleCodes: UserRole[];
  /** Có nhiều role không */
  isMultiRole: boolean;
  /** Đang loading */
  loading: boolean;
  /** Lỗi nếu có */
  error: string | null;
  /** Refresh */
  refresh: () => void;
}

export function useUserRoles(userId: string | null, primaryRole: UserRole): UserRolesReturn {
  const [allRoleCodes, setAllRoleCodes] = useState<UserRole[]>([primaryRole]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!userId) {
      setAllRoleCodes([primaryRole]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('user_roles')
        .select('role_code')
        .eq('user_id', userId);

      if (err) throw err;

      const dbRoles = (data ?? []).map((r: any) => r.role_code as UserRole).filter(Boolean);

      const merged = [...new Set([primaryRole, ...dbRoles])];
      setAllRoleCodes(merged);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi tải roles');
      setAllRoleCodes([primaryRole]);
    } finally {
      setLoading(false);
    }
  }, [userId, primaryRole]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    primaryRole,
    allRoleCodes,
    isMultiRole: allRoleCodes.length > 1,
    loading,
    error,
    refresh: fetchRoles,
  };
}
